<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useRoute, useRouter, type LocationQuery } from "vue-router";
  import { Archive, Undo2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Badge } from "@/components/ui/badge";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { StatusTabs } from "@/components/episto-ui/status-tabs";
  import TwoPanePage from "@templates/TwoPanePage.vue";
  import {
    useMAssetLibraryStore,
    type AssetLibraryFilters,
    type LibrarySort,
    type LibraryView,
    type MediaTypeFilter,
  } from "@stores/mAssetLibrary";
  import MediaLibraryFiltersList from "@/components/Admin/MediaLibrary/MediaLibraryFiltersList.vue";
  import ImageLibraryToolbar from "@/components/MCampaign/ImageLibrary/ImageLibraryToolbar.vue";
  import ImageLibraryGrid from "@/components/MCampaign/ImageLibrary/ImageLibraryGrid.vue";
  import ImageLibraryEditForm from "@/components/MCampaign/ImageLibrary/ImageLibraryEditForm.vue";
  import ImageLibraryUploadDialog from "@/components/MCampaign/ImageLibrary/ImageLibraryUploadDialog.vue";
  import ImageLibrarySelectionRail from "@/components/MCampaign/ImageLibrary/ImageLibrarySelectionRail.vue";
  import ImageLibraryBulkTagsDialog from "@/components/MCampaign/ImageLibrary/ImageLibraryBulkTagsDialog.vue";
  import ImageLibraryLightbox from "@/components/MCampaign/ImageLibrary/ImageLibraryLightbox.vue";

  const SORTS: LibrarySort[] = ["most_recent", "most_used", "a_z"];
  const VIEWS: LibraryView[] = ["in", "untagged", "duplicates", "out"];
  const MEDIA_TYPES: MediaTypeFilter[] = ["all", "image", "video"];

  const VIEW_TITLES: Record<LibraryView, string> = {
    in: "Media library",
    untagged: "Untagged assets",
    duplicates: "Duplicated files",
    out: "Removed from library",
  };

  const route = useRoute();
  const router = useRouter();

  const assetLibraryStore = useMAssetLibraryStore();
  const { filters, membership, view, mediaType, totalCount, editedAsset, selectedIds } = storeToRefs(assetLibraryStore);
  const { open, hideFromLibrary, restoreToLibrary, setEditedAsset, setMediaType } = assetLibraryStore;

  const isUploadOpen = ref(false);

  const title = computed(() => VIEW_TITLES[view.value]);
  const mediaTypeTabs = [
    { value: "all", label: "All" },
    { value: "image", label: "Images" },
    { value: "video", label: "Videos" },
  ];

  const queryValue = (value: LocationQuery[string]) => (Array.isArray(value) ? value[0] : value) ?? "";
  const oneOf = <T extends string>(value: string, allowed: T[]) => (allowed.includes(value as T) ? (value as T) : undefined);

  const filtersFromQuery = (query: LocationQuery): Partial<AssetLibraryFilters> => ({
    search: queryValue(query.q),
    tags: queryValue(query.tags).split(",").filter(Boolean),
    sortBy: oneOf(queryValue(query.sort), SORTS),
    view: oneOf(queryValue(query.view), VIEWS),
    tagsMode: queryValue(query.match) === "any" ? "any" : undefined,
    mediaType: oneOf(queryValue(query.type), MEDIA_TYPES),
  });

  const queryFromFilters = (current: AssetLibraryFilters) => ({
    ...(current.search ? { q: current.search } : {}),
    ...(current.tags.length ? { tags: current.tags.join(",") } : {}),
    ...(current.sortBy !== "most_recent" ? { sort: current.sortBy } : {}),
    ...(current.view !== "in" ? { view: current.view } : {}),
    ...(current.tagsMode !== "all" ? { match: current.tagsMode } : {}),
    ...(current.mediaType !== "all" ? { type: current.mediaType } : {}),
  });

  onMounted(() => open("manage", { initialFilters: filtersFromQuery(route.query) }));

  watch(filters, (next) => router.replace({ query: queryFromFilters(next) }));

  const selectionLabel = computed(() => {
    const count = selectedIds.value.length;
    return `${count} asset${count > 1 ? "s" : ""}`;
  });

  const removeSelection = () => hideFromLibrary([...selectedIds.value]);

  const restoreSelection = () => restoreToLibrary([...selectedIds.value]);
</script>

<template>
  <TwoPanePage>
    <template #left>
      <MediaLibraryFiltersList @upload="isUploadOpen = true" />
    </template>

    <template #right>
      <Card class="flex h-[calc(100vh-40px)] flex-col gap-0 overflow-hidden py-0">
        <CardHeader class="flex flex-col gap-4 border-b px-6 pb-4 pt-5">
          <div class="flex w-full items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <CardTitle class="heading-title">{{ title }}</CardTitle>
              <Badge variant="outline">{{ totalCount }} asset{{ totalCount === 1 ? "" : "s" }}</Badge>
            </div>
            <StatusTabs
              :model-value="mediaType"
              :tabs="mediaTypeTabs"
              @update:model-value="(value) => setMediaType(value as MediaTypeFilter)" />
          </div>
          <ImageLibraryToolbar class="w-full" />
        </CardHeader>

        <CardContent class="min-h-0 flex-1 overflow-y-auto p-6">
          <ImageLibraryGrid />
        </CardContent>

        <ImageLibrarySelectionRail v-if="selectedIds.length > 0">
          <template #actions>
            <ImageLibraryBulkTagsDialog />
            <Button
              v-if="membership === 'out'"
              size="sm"
              @click="restoreSelection">
              <Undo2 class="mr-1 h-4 w-4" />
              Put back in library
            </Button>
            <ConfirmDialog
              v-else
              :title="`Remove ${selectionLabel} from library?`"
              confirm-label="Remove"
              @confirm="removeSelection">
              <template #trigger>
                <Button
                  variant="outline"
                  size="sm"
                  class="text-status-error-strong">
                  <Archive class="mr-1 h-4 w-4" />
                  Remove from library
                </Button>
              </template>
              {{
                selectedIds.length > 1
                  ? "They stay in the campaigns already using them, and you can put them back later."
                  : "It stays in the campaigns already using it, and you can put it back later."
              }}
            </ConfirmDialog>
          </template>
        </ImageLibrarySelectionRail>
      </Card>
    </template>
  </TwoPanePage>

  <Sheet
    :open="!!editedAsset"
    @update:open="(open) => !open && setEditedAsset(null)">
    <SheetContent
      side="right"
      class="flex w-96 flex-col gap-0 p-0 sm:max-w-96">
      <SheetHeader class="border-b px-4 py-3 pr-12">
        <SheetTitle class="heading-section">Edit asset</SheetTitle>
        <SheetDescription class="sr-only">Rename, retag or remove this asset from the library.</SheetDescription>
      </SheetHeader>
      <ImageLibraryEditForm v-if="editedAsset" />
    </SheetContent>
  </Sheet>

  <ImageLibraryUploadDialog
    v-model:open="isUploadOpen"
    accept-videos />
  <ImageLibraryLightbox />
</template>
